import React from 'react';
import { motion } from 'framer-motion';
import SEO from '@/components/SEO';

const OurStory = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAFAFA' }}>
      <SEO
        title="Our Story"
        description="The story behind Navs Art Studio — a passion-led studio crafting one-of-a-kind resin and textured wall art inspired by ocean, nature, and automotive design."
        path="/story"
      />
      <div className="pt-32 pb-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl mb-8"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="story-title"
            >
              Our Story
            </h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-lg max-w-none"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            <p className="text-[#1A1A1A] text-lg leading-relaxed mb-8">
              Navs Art Studio was born from a simple belief: art should do more than decorate a space — it should evoke emotion and tell a story.
            </p>

            <p className="text-[#4A4A4A] text-lg leading-relaxed mb-8">
              What began as a personal passion for creation has evolved into a studio dedicated to handcrafted, one-of-a-kind artworks rooted in elegance and individuality. Inspired by nature, movement, and modern luxury, each piece is thoughtfully designed to feel both timeless and deeply personal.
            </p>

            <p className="text-[#4A4A4A] text-lg leading-relaxed mb-8">
              Every artwork is carefully handcrafted, with close attention to texture, detail, and composition. From expressive resin art and refined mini frames to bold statement pieces, our creations reflect patience, creativity, and refined craftsmanship.
            </p>

            <p className="text-[#1A1A1A] text-lg leading-relaxed">
              At Navs Art Studio, art is never mass-produced. Each piece is curated with intention — designed to elevate spaces and resonate with those who value beauty, quality, and originality.
            </p>
          </motion.div>

          {/* Featured Image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-16"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_navs-art-studio/artifacts/j2uwmz8f_68BB0444-5D6B-4E65-94A6-BFF19BB84356.JPG"
              alt="Navs Art Studio Artwork"
              className="w-full rounded-lg"
            />
          </motion.div>

          {/* Philosophy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-24 text-center"
          >
            <h2
              className="text-3xl sm:text-4xl mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Our Philosophy
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mt-12">
              <div>
                <h3 className="text-sm uppercase tracking-widest mb-4">Handcrafted</h3>
                <p className="text-[#4A4A4A] text-sm leading-relaxed">
                  Every piece is made by hand with meticulous attention to detail and texture.
                </p>
              </div>
              <div>
                <h3 className="text-sm uppercase tracking-widest mb-4">One-of-a-Kind</h3>
                <p className="text-[#4A4A4A] text-sm leading-relaxed">
                  No two pieces are exactly alike. Each artwork is unique and unrepeatable.
                </p>
              </div>
              <div>
                <h3 className="text-sm uppercase tracking-widest mb-4">Timeless</h3>
                <p className="text-[#4A4A4A] text-sm leading-relaxed">
                  Designed to be cherished for years, transcending trends and seasons.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default OurStory;
